import { useEffect, useState } from "react";
import cn from "classnames";
import { useSwiperSlide } from "swiper/react";

import { ComponentType, StrapiComponentType } from "@/src/type";

import { SectionModule, TextBreakpointModule } from "@/src/component/partial/__module__";
import { NestedItem } from "./NestedItem";

import styles from "./VerticalNested.module.scss";
import VerticalNestedSwitch from "./VerticalNestedSwitch";

interface VerticalNestedProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.VerticalNested> {}

function VerticalNested({
  lazy,
  section,
  slug,
  snapToGrid,
  bottomDrawerHeight,
  classes,
  searchParams,
  mobileHint,
  deviceWidthHint,
  quality,
  sectionTypePath,
}: VerticalNestedProps) {
  const swiperSlide = useSwiperSlide();
  const [selectedItem, setSelectedItem] = useState<string>("init");
  const [immediateOff, setImmediateOff] = useState<boolean>(false);
  const [fullLoad, setFullLoad] = useState<boolean>(false);
  const verticalNested: StrapiComponentType.Section.VerticalNested = section.type?.[0];
  const isActive = swiperSlide.isActive;

  useEffect(() => {
    window.addEventListener("popstate", () => {
      setImmediateOff(true);
      setSelectedItem("off");
    });
  }, []);

  useEffect(() => {
    if (immediateOff) {
      setImmediateOff(false);
    }
  }, [isActive, immediateOff]);

  useEffect(() => {
    if (fullLoad) {
      return;
    }

    if (isActive) {
      setFullLoad(true);
    }
  }, [isActive, fullLoad]);

  const getChildItems = (lookup: string = "") => {
    return (verticalNested.childCarouselItems ?? []).filter((opt) => opt.itemReferenceName === lookup);
  };

  const columns = () => {
    switch ((verticalNested.carouselItems ?? []).length) {
      case 2:
        return styles.two;
      case 3:
        return styles.three;
      case 4:
        return styles.four;
      default:
        return;
    }
  };

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: classes?.section,
        styledContainer: cn(styles.styledContainer),
      }}
      stylePrefix="VerticalNested"
      lazy={lazy}
      styleContainerId={verticalNested.id}
      snapToGrid={snapToGrid}
      bottomDrawerHeight={bottomDrawerHeight}
      searchParams={searchParams}
      mobileHint={mobileHint}
      deviceWidthHint={deviceWidthHint}
      quality={quality}
    >
      <TextBreakpointModule
        id={`VerticalNested_title_${verticalNested.id}`}
        text={section.title}
        className={styles.title}
        sectionTypePath={sectionTypePath}
      />
      <div className={cn(styles.nestedContainer, columns())}>
        {(verticalNested?.carouselItems ?? []).map((item) => (
          <VerticalNestedSwitch
            key={item.id}
            className={cn(styles.nestedItem, columns(), selectedItem === `${item.id}` && styles.expand)}
            cta={item.cta}
          >
            <NestedItem
              fullLoad={fullLoad}
              key={item.id}
              item={item}
              childItems={getChildItems(item.itemReferenceName)}
              lazy={lazy}
              quality={quality}
              isExpanded={isActive ? selectedItem === `${item.id}` : false}
              expandedItem={selectedItem}
              handleOpen={item.cta?.href && item.cta?.href !== "#" ? () => {} : () => setSelectedItem(`${item.id}`)}
              handleClose={item.cta?.href && item.cta?.href !== "#" ? () => {} : () => setSelectedItem("off")}
              immediateOff={immediateOff}
              totalItems={verticalNested.carouselItems?.length}
            />
          </VerticalNestedSwitch>
        ))}
      </div>
    </SectionModule>
  );
}

export default VerticalNested;
